<template>
  <div class="space-y-6">
    <!-- 헤더 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">크레딧 발행정보</h2>
          <p class="page-subtitle">
            3개월 이내 발행된 크레딧 채권 정보를 조회합니다
          </p>
        </div>
      </div>

      <div class="page-header mt-6">
        <div class="flex items-center justify-between">
          <p class="text-sm text-gray-600 mt-1">총 {{ issuanceData.length }}건</p>
          <button @click="handleBatchUpdate" :disabled="isBatchUpdating || reviewRequiredCount === 0"
            class="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isBatchUpdating }"></i>
            <span>{{ isBatchUpdating ? '업데이트 중...' : 'INFOMAX 주관사 일괄 업데이트' }}</span>
          </button>
        </div>
      </div>

      <!-- 크레딧 타입 선택 -->
      <div class="mb-6">
        <div class="flex flex-wrap gap-2">
          <button v-for="creditType in creditTypes" :key="creditType.type" @click="selectCreditType(creditType.type)"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              currentCreditType === creditType.type
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            ]">
            {{ creditType.title }}
          </button>
        </div>

        <!-- 통계 정보 -->
        <div v-if="!isLoading && issuanceData.length > 0" class="mt-4 space-y-2">
          <div class="flex items-center space-x-2 text-sm">
            <i class="fas fa-calendar-day text-blue-500"></i>
            <span class="text-gray-700">
              선택일 <span class="font-semibold text-blue-600">{{ todayIssuedCount }}</span>개의
              <span class="font-medium">{{ getCurrentCreditTypeName() }}</span>가 발행되었습니다.
            </span>
          </div>

          <!-- 검토 상태 메시지 -->
          <div class="flex items-center space-x-2 text-sm">
            <i v-if="reviewRequiredCount === 0" class="fas fa-check-circle text-green-500"></i>
            <i v-else class="fas fa-exclamation-triangle text-yellow-500"></i>
            <span v-if="reviewRequiredCount === 0" class="text-gray-700">
              <span class="font-medium text-green-600">모든 인수단 검토가 완료</span>되었습니다.
            </span>
            <span v-else class="text-gray-700">
              <span class="font-semibold text-yellow-600">{{ reviewRequiredCount }}</span>개의
              <span class="font-medium text-yellow-700">인수단 검토가 필요</span>합니다.
            </span>
          </div>
        </div>
      </div>
      <!-- 로딩 상태 -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="loading-spinner"></div>
        <span class="ml-3 text-gray-600">발행정보를 불러오는 중...</span>
      </div>

      <!-- 데이터 없음 -->
      <div v-else-if="!issuanceData.length" class="text-center py-12 text-gray-500">
        <i class="fas fa-file-invoice text-4xl mb-4"></i>
        <p>해당 기간에 발행된 채권이 없습니다.</p>
      </div>

      <!-- 테이블 -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                채권코드
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                채권명
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                발행일
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                만기일
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                쿠폰금리
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                주관사
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                인수회사
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(item, index) in sortedIssuanceData" :key="index" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <button @click="copyToClipboard(item.code)"
                  class="text-sm font-mono text-gray-900 hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                  :title="'클릭하여 ' + item.code + ' 복사'">
                  {{ item.code }}
                </button>
              </td>
              <td class="px-6 py-4">
                <button @click="copyToClipboard(item.code)"
                  class="text-sm text-gray-900 max-w-xs hover:text-blue-600 hover:underline cursor-pointer transition-colors text-left"
                  :title="'클릭하여 ' + item.code + ' 복사'">
                  {{ item.bondName }}
                </button>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ formatDate(item.issueDate) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ formatDate(item.maturityDate) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ item.couponYield !== null ? item.couponYield : '-' }}
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900">
                  <div v-if="item.leadManagers && item.leadManagers.length > 0" class="space-y-1">
                    <div v-for="(manager, idx) in item.leadManagers" :key="idx" class="flex items-center text-xs">
                      <span
                        class="inline-flex items-center justify-center w-4 h-4 bg-blue-100 text-blue-800 text-xs font-medium rounded-full mr-2 flex-shrink-0">
                        {{ idx + 1 }}
                      </span>
                      <span class="truncate">{{ manager }}</span>
                    </div>
                  </div>
                  <div v-else class="text-gray-400 text-xs">-</div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900">
                  <div v-if="item.underwriters && item.underwriters.length > 0" class="space-y-1">
                    <div v-for="(underwriter, idx) in item.underwriters" :key="idx" class="flex items-center text-xs">
                      <span
                        class="inline-flex items-center justify-center w-4 h-4 bg-green-100 text-green-800 text-xs font-medium rounded-full mr-2 flex-shrink-0">
                        {{ idx + 1 }}
                      </span>
                      <span class="truncate">{{ underwriter }}</span>
                    </div>
                  </div>
                  <div v-else class="text-gray-400 text-xs">-</div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button @click="toggleStatus(item)" :disabled="isUpdatingStatus" :class="[
                  getStatusBadgeClass(item.status),
                  'cursor-pointer hover:opacity-80 transition-opacity disabled:cursor-not-allowed disabled:opacity-50'
                ]">
                  {{ getStatusText(item.status) }}
                  <i v-if="isUpdatingStatus && updatingItemCode === item.code"
                    class="fas fa-spinner fa-spin ml-1 text-xs"></i>
                </button>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <button @click="openEditModal(item)"
                  class="text-blue-600 hover:text-blue-900 text-sm font-medium hover:underline">
                  수정
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 발행정보 테이블 -->

    <!-- 수정 모달 -->
    <BaseModal v-model="showEditModal" title="발행정보 수정" size="xl" @close="closeEditModal">
      <div v-if="selectedBond" class="space-y-6">
        <!-- 채권 기본 정보 (전체 너비) -->
        <div class="border rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-3">채권 정보</h3>
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">채권코드:</span>
              <span class="ml-2 font-mono font-medium">{{ selectedBond.code }}</span>
            </div>
            <div>
              <span class="text-gray-500">채권명:</span>
              <span class="ml-2 font-medium">{{ selectedBond.bondName }}</span>
            </div>
          </div>
        </div>

        <!-- 좌우 분할 영역 -->
        <div class="grid grid-cols-2 gap-8">
          <!-- 좌측: 편집 영역 -->
          <div class="space-y-4">
            <!-- 상태 수정 -->
            <div>
              <select v-model="editForm.status" class="select-base">
                <option value="OK">검토완료</option>
                <option value="CREATED">검토필요</option>
              </select>
            </div>

            <!-- 쿠폰금리 & 듀레이션 편집 -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">쿠폰금리</label>
                <input v-model="editForm.couponYield" type="number" step="0.01" class="input-base w-full"
                  placeholder="쿠폰금리 입력" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">듀레이션</label>
                <input v-model="editForm.duration" type="number" step="0.01" class="input-base w-full"
                  placeholder="듀레이션 입력" />
              </div>
            </div>

            <!-- 주관사 수정 -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="block text-sm font-medium text-gray-700">주관사</label>
                <button type="button" @click="addLeadManager" class="text-gray-600 hover:text-gray-800 text-sm">
                  + 추가
                </button>
              </div>
              <div class="space-y-2 min-h-[140px]">
                <div v-for="(manager, index) in editForm.leadManagers" :key="index" class="flex items-center space-x-2">
                  <span
                    class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 text-xs font-medium rounded-full flex-shrink-0">
                    {{ index + 1 }}
                  </span>
                  <input v-model="editForm.leadManagers[index]" type="text" class="input-base flex-1"
                    :placeholder="`주관사 ${index + 1}`" />
                  <button type="button" @click="removeLeadManager(index)" class="text-gray-400 hover:text-red-500 p-1">
                    <i class="fas fa-times text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 인수회사 수정 -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="block text-sm font-medium text-gray-700">인수회사</label>
                <button type="button" @click="addUnderwriter" class="text-gray-600 hover:text-gray-800 text-sm">
                  + 추가
                </button>
              </div>
              <div class="space-y-2 min-h-[140px]">
                <div v-for="(underwriter, index) in editForm.underwriters" :key="index"
                  class="flex items-center space-x-2">
                  <span
                    class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 text-xs font-medium rounded-full flex-shrink-0">
                    {{ index + 1 }}
                  </span>
                  <input v-model="editForm.underwriters[index]" type="text" class="input-base flex-1"
                    :placeholder="`인수회사 ${index + 1}`" />
                  <button type="button" @click="removeUnderwriter(index)" class="text-gray-400 hover:text-red-500 p-1">
                    <i class="fas fa-times text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 우측: 인포맥스 정보 영역 -->
          <div class="space-y-4">
            <!-- 인포맥스 API 조회 -->
            <div>
              <button type="button" @click="fetchInfomaxData" :disabled="isFetchingInfomax" class="w-full btn-secondary"
                :class="{ 'opacity-50 cursor-not-allowed': isFetchingInfomax }">
                <div class="flex items-center justify-center space-x-2">
                  <i class="fas fa-search" :class="{ 'fa-spinner fa-spin': isFetchingInfomax }"></i>
                  <span>{{ isFetchingInfomax ? '조회 중...' : '인포맥스 발행정보 조회' }}</span>
                </div>
              </button>
            </div>

            <!-- 인포맥스 조회 결과 -->
            <div v-if="infomaxData.leadManagers || infomaxData.underwriters" class="space-y-4">
              <!-- 인포맥스 쿠폰금리 & 듀레이션 -->
              <div class="border rounded-lg p-2 bg-blue-50">
                <h4 class="text-sm font-medium text-gray-900 mb-3">인포맥스 조회 정보</h4>
                <div class="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span class="text-gray-600">쿠폰금리:</span>
                    <span class="ml-2 font-medium text-gray-900">
                      {{ infomaxData.couponYield !== null && infomaxData.couponYield !== undefined ?
                        infomaxData.couponYield : '-' }}
                    </span>
                  </div>
                  <div>
                    <span class="text-gray-600">듀레이션:</span>
                    <span class="ml-2 font-medium text-gray-900">
                      {{ infomaxData.duration !== null && infomaxData.duration !== undefined ? infomaxData.duration :
                      '-' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 인포맥스 주관사 -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700">인포맥스 주관사</label>
                  <button type="button" @click="mergeLeadManagers"
                    class="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-sm">
                    <i class="fas fa-arrow-left"></i>
                    <span>좌측으로 복사</span>
                  </button>
                </div>
                <div class="space-y-2 border rounded-lg p-3 min-h-[140px]">
                  <div v-for="(manager, index) in infomaxData.leadManagers" :key="index"
                    class="flex items-center space-x-2">
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 text-xs font-medium rounded-full flex-shrink-0">
                      {{ index + 1 }}
                    </span>
                    <div class="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm">
                      {{ manager }}
                    </div>
                  </div>
                  <div v-if="!infomaxData.leadManagers?.length" class="text-gray-500 text-sm text-center py-2">
                    조회된 주관사 정보가 없습니다.
                  </div>
                </div>
              </div>

              <!-- 인포맥스 인수회사 -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700">인포맥스 인수회사</label>
                  <button type="button" @click="mergeUnderwriters"
                    class="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-sm">
                    <i class="fas fa-arrow-left"></i>
                    <span>좌측으로 복사</span>
                  </button>
                </div>
                <div class="space-y-2 border rounded-lg p-3 min-h-[140px]">
                  <div v-for="(underwriter, index) in infomaxData.underwriters" :key="index"
                    class="flex items-center space-x-2">
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 text-xs font-medium rounded-full flex-shrink-0">
                      {{ index + 1 }}
                    </span>
                    <div class="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm">
                      {{ underwriter }}
                    </div>
                  </div>
                  <div v-if="!infomaxData.underwriters?.length" class="text-gray-500 text-sm text-center py-2">
                    조회된 인수회사 정보가 없습니다.
                  </div>
                </div>
              </div>

              <!-- 전체 병합 버튼 -->
              <div class="border-t pt-4">
                <button type="button" @click="mergeAllInfomaxData"
                  class="w-full bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                  <div class="flex items-center justify-center space-x-2">
                    <i class="fas fa-arrow-left"></i>
                    <span>모든 정보 좌측으로 병합</span>
                  </div>
                </button>
              </div>
            </div>

            <!-- 조회 전 안내 메시지 -->
            <div v-else class="flex items-center justify-center py-8">
              <div class="text-center text-gray-500">
                <i class="fas fa-info-circle text-2xl mb-2"></i>
                <p class="text-sm">인포맥스 발행정보를 조회하여<br>기존 정보와 비교해보세요.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="closeEditModal">취소</BaseButton>
        <BaseButton variant="primary" @click="saveChanges" :loading="isSaving">저장</BaseButton>
      </template>
    </BaseModal>

    <!-- 로딩 오버레이 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="bg-white rounded-xl p-8 shadow-2xl">
        <div class="flex items-center space-x-4">
          <div class="loading-spinner"></div>
          <span class="text-gray-700 font-medium">발행정보를 불러오는 중...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useCreditIssuance } from '@/composables/useCreditIssuance';
import { useClipboard } from '@/composables/useClipboard';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { useBondStatus } from '@/composables/useBondStatus';
import BaseModal from '@/components/common/BaseModal.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import bondService from '@/service/bondService';

export default {
  name: 'CreditIssuanceComponent',
  components: {
    BaseModal,
    BaseButton,
  },
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {
      isLoading,
      currentCreditType,
      issuanceData,
      creditTypes,
      selectCreditType,
    } = useCreditIssuance();

    // 클립보드 기능
    const { copyToClipboard } = useClipboard();

    // 날짜 포맷팅 기능
    const { formatDate } = useDateFormatter();

    // 상태 관련 기능
    const { getStatusText, getStatusBadgeClass } = useBondStatus();

    // 검토필요 -> 검토완료 순, 그 다음 최근 발행순으로 정렬된 데이터
    const sortedIssuanceData = computed(() => {
      return [...issuanceData.value].sort((a, b) => {
        // 1차 정렬: 상태 (CREATED(검토필요) -> OK(검토완료))
        const statusOrder = { 'CREATED': 0, 'OK': 1 };
        const statusA = statusOrder[a.status] ?? 2;
        const statusB = statusOrder[b.status] ?? 2;

        if (statusA !== statusB) {
          return statusA - statusB;
        }

        // 2차 정렬: 발행일 역순 (최신순)
        const dateA = new Date(a.issueDate);
        const dateB = new Date(b.issueDate);
        return dateB - dateA;
      });
    });

    // 통계 계산
    const todayIssuedCount = computed(() => {
      const selectedDateStr = props.selectedDate;
      return issuanceData.value.filter(item => {
        const issueDate = new Date(item.issueDate).toISOString().split('T')[0];
        return issueDate === selectedDateStr;
      }).length;
    });

    const reviewRequiredCount = computed(() => {
      return issuanceData.value.filter(item => item.status === 'CREATED').length;
    });

    const getCurrentCreditTypeName = () => {
      const currentType = creditTypes.value.find(type => type.type === currentCreditType.value);
      return currentType ? currentType.title : '';
    };

    // 모달 관련 상태
    const showEditModal = ref(false);
    const selectedBond = ref(null);
    const isFetchingInfomax = ref(false);
    const isSaving = ref(false);
    const isUpdatingStatus = ref(false);
    const updatingItemCode = ref(null);
    const isBatchUpdating = ref(false);
    // 편집 폼 데이터
    const editForm = ref({
      status: '',
      couponYield: null,
      duration: null,
      leadManagers: [],
      underwriters: [],
    });

    // 인포맥스 데이터
    const infomaxData = ref({
      couponYield: null,
      duration: null,
      leadManagers: [],
      underwriters: [],
    });

    // 모달 관련 함수들
    const openEditModal = (bond) => {
      selectedBond.value = bond;
      editForm.value = {
        status: bond.status || '',
        couponYield: bond.couponYield,
        duration: bond.duration,
        leadManagers: [...(bond.leadManagers || [])],
        underwriters: [...(bond.underwriters || [])],
      };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      selectedBond.value = null;
      editForm.value = {
        status: '',
        couponYield: null,
        duration: null,
        leadManagers: [],
        underwriters: [],
      };
      // 인포맥스 데이터도 초기화
      infomaxData.value = {
        couponYield: null,
        duration: null,
        leadManagers: [],
        underwriters: [],
      };
    };

    // 주관사/인수회사 관리 함수들
    const addLeadManager = () => {
      editForm.value.leadManagers.push('');
    };

    const removeLeadManager = (index) => {
      editForm.value.leadManagers.splice(index, 1);
    };

    const addUnderwriter = () => {
      editForm.value.underwriters.push('');
    };

    const removeUnderwriter = (index) => {
      editForm.value.underwriters.splice(index, 1);
    };

    // 인포맥스 API 조회
    const fetchInfomaxData = async () => {
      if (!selectedBond.value?.code) return;

      isFetchingInfomax.value = true;
      try {
        const response = await bondService.fetchIssuanceFromInfomax(selectedBond.value.code);

        // 인포맥스 데이터를 별도로 저장
        infomaxData.value = {
          couponYield: response.couponYield,
          duration: response.duration,
          leadManagers: response.leadManagers || [],
          underwriters: response.underwriters || [],
        };

        alert('인포맥스 정보를 성공적으로 가져왔습니다.');
      } catch (error) {
        console.error('인포맥스 정보 조회 실패:', error);
        alert('인포맥스 정보 조회에 실패했습니다.');
      } finally {
        isFetchingInfomax.value = false;
      }
    };

    // 인포맥스 데이터 병합 함수들
    const mergeLeadManagers = () => {
      if (confirm('인포맥스 주관사 정보를 좌측으로 복사하시겠습니까?\n기존 정보는 덮어쓰여집니다.')) {
        editForm.value.leadManagers = [...infomaxData.value.leadManagers];
      }
    };

    const mergeUnderwriters = () => {
      if (confirm('인포맥스 인수회사 정보를 좌측으로 복사하시겠습니까?\n기존 정보는 덮어쓰여집니다.')) {
        editForm.value.underwriters = [...infomaxData.value.underwriters];
      }
    };

    const mergeAllInfomaxData = () => {
      if (confirm('인포맥스의 모든 정보를 좌측으로 복사하시겠습니까?\n기존 정보는 덮어쓰여집니다.')) {
        editForm.value.couponYield = infomaxData.value.couponYield;
        editForm.value.duration = infomaxData.value.duration;
        editForm.value.leadManagers = [...infomaxData.value.leadManagers];
        editForm.value.underwriters = [...infomaxData.value.underwriters];
      }
    };

    // 변경사항 저장
    const saveChanges = async () => {
      if (!selectedBond.value) return;

      isSaving.value = true;
      try {
        // 빈 문자열 제거
        const cleanedLeadManagers = editForm.value.leadManagers.filter(manager => manager.trim());
        const cleanedUnderwriters = editForm.value.underwriters.filter(underwriter => underwriter.trim());

        const updateData = {
          code: selectedBond.value.code,
          status: editForm.value.status,
          coupon: editForm.value.couponYield,
          duration: editForm.value.duration,
          leadManagers: cleanedLeadManagers,
          underwriters: cleanedUnderwriters,
        };

        // bondService를 통한 API 호출
        await bondService.updateIssuance(updateData);

        // 로컬 데이터 업데이트
        const index = issuanceData.value.findIndex(item => item.code === selectedBond.value.code);
        if (index !== -1) {
          issuanceData.value[index] = {
            ...issuanceData.value[index],
            status: updateData.status,
            couponYield: updateData.coupon,
            duration: updateData.duration,
            leadManagers: updateData.leadManagers,
            underwriters: updateData.underwriters,
          };
        }

        alert('변경사항이 저장되었습니다.');
        closeEditModal();
      } catch (error) {
        console.error('저장 실패:', error);
        alert('저장에 실패했습니다.');
      } finally {
        isSaving.value = false;
      }
    };

    // INFOMAX 주관사 일괄 업데이트
    const handleBatchUpdate = async () => {
      const reviewRequiredItems = issuanceData.value.filter(item => item.status === 'CREATED');

      if (reviewRequiredItems.length === 0) {
        alert('검토필요 상태의 채권이 없습니다.');
        return;
      }

      const codes = reviewRequiredItems.map(item => item.code);
      const confirmMessage = `검토필요 상태의 ${codes.length}개 채권에 대해 INFOMAX 주관사 정보를 일괄 업데이트하시겠습니까?\n\n대상 채권:\n${reviewRequiredItems.map(item => `• ${item.code} - ${item.bondName}`).slice(0, 5).join('\n')}${codes.length > 5 ? `\n... 외 ${codes.length - 5}개` : ''}`;

      if (!confirm(confirmMessage)) {
        return;
      }

      isBatchUpdating.value = true;

      try {
        await bondService.batchUpdateIssuance(codes);

        // 업데이트 후 데이터 다시 로드
        await selectCreditType(currentCreditType.value);

        alert(`${codes.length}개 채권의 INFOMAX 주관사 정보가 성공적으로 업데이트되었습니다.`);
      } catch (error) {
        console.error('일괄 업데이트 실패:', error);
        alert('INFOMAX 주관사 일괄 업데이트에 실패했습니다.');
      } finally {
        isBatchUpdating.value = false;
      }
    };

    // 상태 토글
    const toggleStatus = async (item) => {
      if (isUpdatingStatus.value) return;

      const newStatus = item.status === 'OK' ? 'CREATED' : 'OK';
      const statusText = newStatus === 'OK' ? '검토완료' : '검토필요';

      if (!confirm(`${item.bondName}의 상태를 '${statusText}'로 변경하시겠습니까?`)) {
        return;
      }

      isUpdatingStatus.value = true;
      updatingItemCode.value = item.code;

      try {
        const updateData = {
          code: item.code,
          status: newStatus,
          leadManagers: item.leadManagers || [],
          underwriters: item.underwriters || [],
        };

        await bondService.updateIssuance(updateData);

        // 로컬 데이터 업데이트
        const index = issuanceData.value.findIndex(bond => bond.code === item.code);
        if (index !== -1) {
          issuanceData.value[index].status = newStatus;
        }

        alert(`상태가 '${statusText}'로 변경되었습니다.`);
      } catch (error) {
        console.error('상태 변경 실패:', error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        isUpdatingStatus.value = false;
        updatingItemCode.value = null;
      }
    };







    return {
      // 기존 데이터
      isLoading,
      currentCreditType,
      issuanceData,
      sortedIssuanceData,
      creditTypes,
      selectCreditType,

      // 통계
      todayIssuedCount,
      reviewRequiredCount,
      getCurrentCreditTypeName,

      // 모달 관련
      showEditModal,
      selectedBond,
      editForm,
      isFetchingInfomax,
      isSaving,
      openEditModal,
      closeEditModal,

      // 폼 관리
      addLeadManager,
      removeLeadManager,
      addUnderwriter,
      removeUnderwriter,

      // API 관련
      fetchInfomaxData,
      saveChanges,
      toggleStatus,
      isUpdatingStatus,
      updatingItemCode,
      handleBatchUpdate,
      isBatchUpdating,

      // 인포맥스 관련
      infomaxData,
      mergeLeadManagers,
      mergeUnderwriters,
      mergeAllInfomaxData,

      // 유틸리티
      formatDate,
      copyToClipboard,
      getStatusText,
      getStatusBadgeClass,
    };
  },
};
</script>

<style scoped>
.btn-secondary {
  @apply inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary:focus {
  @apply outline-none ring-2 ring-offset-2 ring-gray-500;
}

.input-base {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm;
}

.select-base {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white;
}

/* 상태 버튼 스타일 개선 */
.status-button {
  @apply inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-offset-2;
}
</style>
